<template>
	<v-card>
		<v-toolbar flat color="dark" dark>
			<v-toolbar-title>
				<span class="pl-2">Toets herkansing — Aanmeldingen</span>
			</v-toolbar-title>
		</v-toolbar>

		<v-alert v-if="!examId" type="info" dense outlined class="ma-4">
			Open deze pagina via het aanmeldingen-icoon bij een toets in het coördinatoroverzicht, of voeg
			<code>?exam_id=…</code> toe aan de URL.
		</v-alert>

		<v-alert v-else-if="examLoadError" type="warning" dense outlined class="ma-4">
			{{ examLoadError }}
		</v-alert>

		<v-card-text v-if="examId">
			<p class="subtitle-1 font-weight-medium mb-2">Geselecteerde toets (alleen lezen)</p>
			<v-progress-linear v-if="examLoading" indeterminate color="primary" class="mb-4"></v-progress-linear>
			<v-row v-else dense>
				<v-col v-for="row in summaryFields" :key="row.label" cols="12" sm="6" md="4">
					<v-text-field
						:value="row.value"
						:label="row.label"
						disabled
						filled
						dense
						hide-details
					></v-text-field>
				</v-col>
			</v-row>

			<v-divider class="my-6"></v-divider>

			<p class="subtitle-1 font-weight-medium mb-2 px-4 pt-2">Examenkandidaten</p>
			<p class="body-2 grey--text text--darken-1 mb-0 px-4">
				Aanmeldingen voor toets-ID <strong>{{ examId }}</strong>.
			</p>

			<v-alert v-if="candidatesLoadError" type="warning" dense outlined class="mx-4 mt-4 mb-0">
				{{ candidatesLoadError }}
			</v-alert>

			<v-card-title class="pt-2 pb-0">
				<v-text-field
					v-model="candidatesSearch"
					append-icon="mdi-magnify"
					label="Filter"
					single-line
					hide-details
				></v-text-field>
			</v-card-title>

			<v-data-table
				:headers="candidateHeaders"
				:items="candidates"
				:search="candidatesSearch"
				:loading="candidatesLoading"
				no-data-text="Nog geen aanmeldingen voor deze toets."
			>
				<template v-slot:top>
					<v-toolbar dark dense color="primary" flat class="mb-1">
						<v-tooltip bottom>
							<template v-slot:activator="{ on, attrs }">
								<span v-bind="attrs" v-on="on">
									<v-btn
										depressed
										class="ml-2"
										color="inverted"
										:disabled="!canAddCandidates"
										@click="goAddCandidates"
									>
										<v-icon class="pr-2">mdi-account-multiple-plus</v-icon>
										Aanmeldingen beheren
									</v-btn>
								</span>
							</template>
							<span v-if="examLocked">Toets is vergrendeld.</span>
							<span v-else>Studenten toevoegen of aanmeldingen wijzigen</span>
						</v-tooltip>
					</v-toolbar>
				</template>
				<template v-slot:item.actions="{ item }">
					<v-tooltip bottom>
						<template v-slot:activator="{ on, attrs }">
							<span v-bind="attrs" v-on="on">
								<v-icon
									small
									:color="examLocked ? 'grey' : 'error'"
									:style="examLocked ? { cursor: 'not-allowed' } : { cursor: 'pointer' }"
									:disabled="examLocked || deleteSubmitting"
									@click="requestDelete(item)"
								>
									mdi-delete-outline
								</v-icon>
							</span>
						</template>
						<span>{{ examLocked ? "Toets is vergrendeld." : "Aanmelding verwijderen" }}</span>
					</v-tooltip>
				</template>
				<template v-slot:item.createdAt="{ item }">
					{{ formatRegistrationDate(item.createdAt) }}
				</template>
			</v-data-table>
		</v-card-text>

		<v-dialog v-model="deleteDialog" max-width="420" persistent>
			<v-card v-if="pendingDeleteItem">
				<v-card-title class="headline">Aanmelding verwijderen?</v-card-title>
				<v-card-text class="pt-2">
					De aanmelding van
					<strong>{{ pendingDeleteStudentName }}</strong>
					wordt verwijderd. Dit kan niet ongedaan worden gemaakt.
				</v-card-text>
				<v-alert v-if="deleteError" type="error" dense outlined class="mx-4">
					{{ deleteError }}
				</v-alert>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text :disabled="deleteSubmitting" @click="cancelDelete">Annuleren</v-btn>
					<v-btn color="error" depressed :loading="deleteSubmitting" @click="confirmDelete">
						Verwijderen
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-card>
</template>

<script>
import Axios from "axios";
import { API_ROOT } from "../../config";

const ax = Axios.create({
	baseURL: API_ROOT["osbod_salary"],
	timeout: 60000
});

// Same list style as StudentOvertakeClass / ExamCoordinator (nested Sequelize paths on rows)
const CANDIDATE_HEADERS = [
	{ text: "", value: "actions", align: "center", sortable: false, width: "52px" },
	{ text: "ID", value: "id", align: "end", width: "72px" },
	{ text: "Student", value: "tbl_student.display_name" },
	{ text: "Omschrijving", value: "description" },
	{ text: "Aangemeld door", value: "tbl_user.display_name" },
	{ text: "Aangemeld op", value: "createdAt" }
];

export default {
	name: "ExamAanmeldingen",
	data() {
		return {
			examId: null,
			examDetail: null,
			examLoading: false,
			examLoadError: "",
			candidateHeaders: CANDIDATE_HEADERS,
			candidates: [],
			candidatesLoading: false,
			candidatesLoadError: "",
			candidatesSearch: "",
			deleteDialog: false,
			pendingDeleteItem: null,
			deleteSubmitting: false,
			deleteError: ""
		};
	},
	computed: {
		pendingDeleteStudentName() {
			if (!this.pendingDeleteItem) return "";
			var st = this.pendingDeleteItem.tbl_student;
			if (!st) return "";
			return (
				st.display_name ||
				[String(st.firstname || "").trim(), String(st.surname || "").trim()].filter(Boolean).join(" ") ||
				"—"
			);
		},
		summaryFields() {
			if (!this.examDetail) return [];
			var pick = this.pickExamField.bind(this);
			var d = this.examDetail;
			return [
				{
					label: "Toets-ID",
					value: pick(d, ["id", "exam_id", "tbl_exam.id"])
				},
				{
					label: "Examen",
					value: pick(d, [
						"tbl_sys_exam_type.name",
						"tbl_exam_type.name",
						"exam_type.name",
						"tbl_sys_exam_type_name",
						"exam_type_name",
						"examen"
					])
				},
				{
					label: "Sector",
					value: pick(d, [
						"tbl_sys_sector.name",
						"sector.name",
						"tbl_sys_sector_name",
						"sector_name"
					])
				},
				{
					label: "Opleiding",
					value: pick(d, [
						"tbl_sys_study.name",
						"study.name",
						"tbl_sys_study_name",
						"study_name",
						"opleiding"
					])
				},
				{
					label: "Klas",
					value: pick(d, [
						"tbl_sys_class.name",
						"class.name",
						"tbl_sys_class_name",
						"class_name",
						"klas"
					])
				},
				{
					label: "Periode",
					value: pick(d, [
						"tbl_sys_periode.name",
						"periode.name",
						"tbl_sys_periode_name",
						"periode_name"
					])
				},
				{
					label: "Deelkwalificatie",
					value: pick(d, [
						"tbl_sys_subject.name",
						"subject.name",
						"tbl_sys_subject_name",
						"subject_name",
						"deelkwalificatie"
					])
				},
				{
					label: "Module",
					value: pick(d, [
						"tbl_teacher_subject_class.module",
						"teacher_subject_class.module",
						"module",
						"module_name"
					])
				},
				{
					label: "Docent",
					value: pick(d, [
						"tbl_employee.teacher_name",
						"employee.teacher_name",
						"tbl_employee.display_name",
						"teacher_name",
						"docent",
						"docent_naam"
					])
				}
			];
		},
		examLocked() {
			if (!this.examDetail || this.examDetail.locked == null) return false;
			return (
				Number(this.examDetail.locked) === 1 ||
				this.examDetail.locked === true ||
				this.examDetail.locked === "1"
			);
		},
		canAddCandidates() {
			if (!this.examId || this.examLoading || this.examLoadError) return false;
			return !this.examLocked;
		}
	},
	watch: {
		"$route.query.exam_id": function() {
			this.loadExamContext();
		}
	},
	created() {
		this.loadExamContext();
	},
	methods: {
		goAddCandidates() {
			if (!this.examId || this.examLocked) return;
			this.$router.push({
				path: "/schooladmin/exam_aanmeldingen/add",
				query: { exam_id: String(this.examId) }
			});
		},
		requestDelete(item) {
			if (this.examLocked || !item || item.id == null) return;
			this.deleteError = "";
			this.pendingDeleteItem = item;
			this.deleteDialog = true;
		},
		cancelDelete() {
			this.deleteDialog = false;
			this.pendingDeleteItem = null;
			this.deleteError = "";
		},
		confirmDelete() {
			var self = this;
			if (!this.pendingDeleteItem || this.pendingDeleteItem.id == null) return;
			this.deleteSubmitting = true;
			this.deleteError = "";
			var rid = this.pendingDeleteItem.id;
			ax.post("/student_overtake_exam/delete/" + rid)
				.then(function () {
					self.cancelDelete();
					self.loadCandidates();
				})
				.catch(function () {
					self.deleteError =
						"Verwijderen is mislukt. Controleer of de aanmelding nog bestaat en probeer opnieuw.";
				})
				.finally(function () {
					self.deleteSubmitting = false;
				});
		},
		pickExamDisplay(obj, dotPath) {
			if (obj == null || dotPath == null) return null;
			var parts = String(dotPath).split(".");
			var cur = obj;
			for (var i = 0; i < parts.length; i++) {
				if (cur == null || typeof cur !== "object") return null;
				cur = cur[parts[i]];
			}
			if (cur == null || cur === "") return null;
			return String(cur);
		},
		pickExamField(root, paths) {
			if (!root) return "—";
			var sources = [root];
			if (root.tbl_exam && typeof root.tbl_exam === "object") {
				sources.push(root.tbl_exam);
			}
			for (var s = 0; s < sources.length; s++) {
				for (var p = 0; p < paths.length; p++) {
					var v = this.pickExamDisplay(sources[s], paths[p]);
					if (v != null) return v;
				}
			}
			return "—";
		},
		normalizeListPayload(payload) {
			if (payload == null) return [];
			if (Array.isArray(payload)) return payload;
			if (typeof payload === "object") {
				if (Array.isArray(payload.data)) return payload.data;
				if (Array.isArray(payload.records)) return payload.records;
				if (Array.isArray(payload.items)) return payload.items;
				if (Array.isArray(payload.result)) return payload.result;
			}
			return [];
		},
		formatRegistrationDate(value) {
			if (value == null || value === "") return "—";
			try {
				var d = value instanceof Date ? value : new Date(value);
				if (Number.isNaN(d.getTime())) return String(value);
				return d.toLocaleString("nl-NL", {
					year: "numeric",
					month: "2-digit",
					day: "2-digit",
					hour: "2-digit",
					minute: "2-digit"
				});
			} catch (e) {
				return String(value);
			}
		},
		loadCandidates() {
			var self = this;
			if (!this.examId) {
				this.candidates = [];
				this.candidatesLoadError = "";
				return;
			}
			this.candidatesLoading = true;
			this.candidatesLoadError = "";
			ax.get("/student_overtake_exam/all_by_exam", {
				params: { exam_id: this.examId }
			})
				.then(function(response) {
					self.candidates = self.normalizeListPayload(response.data);
				})
				.catch(function() {
					self.candidatesLoadError =
						"Aanmeldingen konden niet worden geladen (student_overtake_exam/all_by_exam).";
					self.candidates = [];
				})
				.finally(function() {
					self.candidatesLoading = false;
				});
		},
		loadExamContext() {
			var q = this.$route.query.exam_id;
			this.examId = q != null && String(q).trim() !== "" ? String(q).trim() : null;
			this.examDetail = null;
			this.examLoadError = "";
			this.candidates = [];
			this.candidatesLoadError = "";
			this.candidatesSearch = "";
			if (!this.examId) {
				return;
			}
			this.loadCandidates();
			var self = this;
			this.examLoading = true;
			ax.get("/exam/single/" + this.examId)
				.then(function(response) {
					var payload = response.data;
					if (
						payload &&
						payload.data != null &&
						typeof payload.data === "object" &&
						!Array.isArray(payload.data)
					) {
						payload = payload.data;
					}
					// /exam/single returns the exam row (optionally wrapped); ignore non-objects
					if (payload == null || typeof payload !== "object" || Array.isArray(payload)) {
						self.examLoadError =
							"Onverwacht antwoord van exam/single. Controleer het toets-ID.";
						self.examDetail = null;
						return;
					}
					self.examDetail = payload;
				})
				.catch(function() {
					self.examLoadError =
						"Toetsgegevens konden niet worden geladen (exam/single). Controleer het toets-ID.";
					self.examDetail = null;
				})
				.finally(function() {
					self.examLoading = false;
				});
		}
	}
};
</script>

<style scoped>
code {
	padding: 0.1em 0.35em;
	background: rgba(0, 0, 0, 0.06);
	border-radius: 4px;
}
</style>
